import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, X, FileText, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const MAX_FILES = 5;

const statusText = item => {
  if (item.kind === 'image') return 'Image ready for the AI';
  if (item.status === 'processing') return item.progress?.total
    ? `Reading document… part ${item.progress.done + 1} of ${item.progress.total}`
    : 'Reading document…';
  if (item.status === 'ready') return `${item.pages} page(s) read${item.condensed ? ' · condensed for the AI' : ''}`;
  return item.message || 'Could not read this file';
};

export const AiAttachments = ({ items, setItems, uploadFile, setForm, disabled }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const polling = useRef(new Set());

  useEffect(() => {
    const pending = items.filter(item => item.kind === 'pdf' && item.status === 'processing' && item.docId && !polling.current.has(item.docId));
    pending.forEach(item => {
      polling.current.add(item.docId);
      const tick = async () => {
        try {
          const response = await fetch(`${API}/api/kb/ai/documents/${item.docId}`, { headers: auth() });
          const data = await response.json();
          if (!response.ok) throw new Error(data.detail || 'Could not read this document');
          setItems(previous => previous.map(entry => entry.docId === item.docId ? { ...entry, ...data } : entry));
          if (data.status === 'processing') return setTimeout(tick, 2500);
          polling.current.delete(item.docId);
          if (data.status !== 'ready') toast.error(data.message || 'That document could not be read');
        } catch (error) {
          polling.current.delete(item.docId);
          setItems(previous => previous.map(entry => entry.docId === item.docId ? { ...entry, status: 'failed', message: error.message } : entry));
        }
      };
      tick();
    });
  }, [items, setItems]);

  const add = async fileList => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length || busy) return;
    if (items.length + chosen.length > MAX_FILES) { toast.error(`Attach up to ${MAX_FILES} files per request`); return; }
    setBusy(true);
    try {
      for (const file of chosen) {
        const isPdf = file.type === 'application/pdf';
        const isImage = file.type.startsWith('image/');
        if (!isPdf && !isImage) { toast.error(`${file.name}: attach a PDF or an image`); continue; }
        const uploaded = await uploadFile(file);
        if (!uploaded) continue;
        if (isImage) {
          setItems(previous => [...previous, { key: uploaded.id, fileId: uploaded.id, meta: uploaded, kind: 'image', status: 'ready' }]);
          continue;
        }
        const response = await fetch(`${API}/api/kb/ai/documents`, {
          method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ file_id: uploaded.id })
        });
        const job = await response.json();
        if (!response.ok) { toast.error(typeof job.detail === 'string' ? job.detail : 'Could not start reading that PDF'); continue; }
        setItems(previous => [...previous, { key: uploaded.id, fileId: uploaded.id, meta: uploaded, kind: 'pdf', docId: job.id, ...job }]);
      }
    } catch (error) {
      toast.error(error.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  const toggleOnPage = (item, checked) => {
    setItems(previous => previous.map(entry => entry.key === item.key ? { ...entry, onPage: checked } : entry));
    setForm(previous => ({
      ...previous,
      attachments: checked
        ? [...(previous.attachments || []).filter(a => a.id !== item.meta.id), item.meta]
        : (previous.attachments || []).filter(a => a.id !== item.meta.id)
    }));
  };

  return (
    <section className="space-y-3 border-t pt-4" data-testid="kb-ai-attachments">
      <div className="flex items-center justify-between gap-2">
        <Label>Photos and PDFs for the AI to read</Label>
        <Button data-testid="kb-ai-attach" size="sm" variant="outline" disabled={busy || disabled || items.length >= MAX_FILES} onClick={() => input.current?.click()}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />}{busy ? 'Uploading…' : 'Attach'}
        </Button>
      </div>
      <input ref={input} data-testid="kb-ai-attach-input" type="file" multiple hidden
        accept="application/pdf,image/png,image/jpeg,image/webp"
        onChange={event => { add(event.target.files); event.target.value = ''; }} />
      <p className="text-xs text-slate-500">Long PDFs are read page by page and condensed, so documents of 200+ pages work. Scans without text are reported instead of silently ignored.</p>
      <ul className="space-y-2">
        {items.map(item => (
          <li key={item.key} data-testid={`kb-ai-attachment-${item.fileId}`} className="rounded-lg border border-slate-200 p-2 space-y-1.5">
            <div className="flex items-start gap-2 min-w-0">
              {item.kind === 'pdf' ? <FileText className="h-4 w-4 shrink-0 text-rose-600" /> : <ImageIcon className="h-4 w-4 shrink-0 text-indigo-600" />}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-800 [overflow-wrap:anywhere]">{item.meta?.filename || item.filename}</p>
                <p data-testid={`kb-ai-attachment-status-${item.fileId}`} className={`text-xs ${item.status === 'ready' ? 'text-emerald-700' : item.status === 'processing' ? 'text-slate-500' : 'text-red-700'}`}>
                  {statusText(item)}
                </p>
              </div>
              <button data-testid={`kb-ai-attachment-remove-${item.fileId}`} title="Remove attachment" className="shrink-0 p-1 text-slate-500 hover:text-red-600"
                onClick={() => { if (item.onPage) toggleOnPage(item, false); setItems(previous => previous.filter(entry => entry.key !== item.key)); }}>
                <X className="h-3 w-3" />
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <Checkbox data-testid={`kb-ai-attachment-onpage-${item.fileId}`} checked={!!item.onPage} onCheckedChange={checked => toggleOnPage(item, !!checked)} />
              Also attach this file to the page
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
};
